import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { blogPosts, publishedBlogPosts } from '../generated/blogPosts';

gsap.registerPlugin(ScrollTrigger);

const { FiArrowUpRight, FiBookOpen, FiTrendingUp, FiUsers } = FiIcons;

const themes = [
  {
    number: '01',
    title: 'Leadership that lasts',
    description: 'Practical perspectives on building accountable teams, developing leaders, and creating a culture people want to sustain.',
    icon: FiUsers,
  },
  {
    number: '02',
    title: 'Sales with discipline',
    description: 'Lessons on coaching, execution, and the operating rhythms behind consistent customer and sales performance.',
    icon: FiTrendingUp,
  },
  {
    number: '03',
    title: 'Operational excellence',
    description: 'How leaders turn complex challenges into clear priorities, measurable outcomes, and stronger customer relationships.',
    icon: FiBookOpen,
  },
];

const reviewPosts = blogPosts
  .filter((post) => post.slug !== 'complete-article-draft-template')
  .slice(0, 3);

const BlogSection = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const cardsRef = useRef(null);
  const visiblePosts = import.meta.env.DEV ? reviewPosts : publishedBlogPosts.slice(0, 3);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headingRef.current, {
        scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' },
        y: 60,
        opacity: 0,
        duration: 0.9,
        ease: 'power3.out',
      });

      gsap.from(cardsRef.current.children, {
        scrollTrigger: { trigger: cardsRef.current, start: 'top 80%' },
        y: 50,
        opacity: 0,
        duration: 0.8,
        stagger: 0.12,
        ease: 'power3.out',
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="blog" ref={sectionRef} className="relative bg-white text-black px-4 md:px-10 py-24 md:py-36 overflow-hidden">
      <div className="max-w-[1600px] mx-auto">
        <div ref={headingRef} className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end border-b border-black pb-10 md:pb-14">
          <div className="md:col-span-7">
            <div className="text-xs font-bold tracking-[0.28em] uppercase text-[#918147] mb-5">Latest insights / Blog</div>
            <h2 className="text-[15vw] md:text-[8vw] font-black tracking-tighter uppercase leading-[0.78]">
              Ideas<br />in practice
            </h2>
          </div>
          <div className="md:col-span-5 md:pb-2">
            <p className="text-lg md:text-2xl font-medium leading-relaxed max-w-xl">
              Katherine shares practical lessons from more than two decades of leading teams, strengthening operations, and building customer-centered organizations.
            </p>
            <a href="/blog/" className="mt-6 inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase border-b border-black pb-1 hover:text-[#918147] hover:border-[#918147] transition-colors">
              Visit the editorial hub <SafeIcon icon={FiArrowUpRight} />
            </a>
          </div>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 border-l border-black mt-12 md:mt-16">
          {(visiblePosts.length ? visiblePosts : themes).map((item, index) => {
            const isPost = Boolean(item.slug);
            const href = isPost ? `/blog/${item.slug}/` : '/blog/';
            return (
            <article key={isPost ? item.slug : item.number} className="group min-h-[420px] md:min-h-[500px] border-r border-b border-t border-black flex flex-col hover:bg-black hover:text-white transition-colors duration-500">
              {isPost && (
                <a href={href} className="block aspect-[16/9] overflow-hidden bg-[#ded8c2]">
                  <img src={item.cover} alt={item.coverAlt} loading="lazy" className="h-full w-full object-cover group-hover:scale-[1.025] transition-transform duration-700" />
                </a>
              )}
              <div className="p-7 md:p-9 flex flex-col flex-1">
              <div className="flex items-center justify-between text-xs font-bold tracking-widest">
                <span>/ {String(index + 1).padStart(2, '0')}{isPost ? ` · ${item.category}` : ''}</span>
                <SafeIcon icon={isPost ? FiBookOpen : item.icon} className="text-xl text-[#918147] group-hover:text-[#e2c96e] transition-colors" />
              </div>
              <div className="mt-auto pt-12">
                <h3 className="text-3xl md:text-4xl font-black uppercase tracking-tight leading-none mb-6">{item.title}</h3>
                <p className="text-sm md:text-base leading-relaxed text-[#4a4a4a] group-hover:text-[#d8d8d8] transition-colors">{isPost ? item.excerpt : item.description}</p>
                {isPost && <a href={href} className="mt-7 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] border-b border-current pb-1 self-start">Read article <SafeIcon icon={FiArrowUpRight} /></a>}
              </div>
              </div>
            </article>
          )})}
        </div>

        <p className="mt-6 text-xs text-[#4a4a4a] leading-relaxed max-w-2xl">
          New perspectives are published regularly across leadership, sales discipline, operations, and customer success.
        </p>
      </div>
    </section>
  );
};

export default BlogSection;
